import { MapViewer } from '@3ddv/dvm-internal';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { Seat } from '@core/models';
import {
  AvailabilityService,
  CartService,
  ConfigurationService,
  DvmService,
  TutorialService,
  UtilitiesService,
} from '@core/services';
import { Subscription } from 'rxjs';
import { IsolatedSeatsService } from './isolated-seats.service';

export type CombinedIds = {
  original_id: string;
  id: string;
};

@Injectable({
  providedIn: 'root',
})
export class SeatManagementService implements OnDestroy {
  private availabilityService: AvailabilityService =
    inject(AvailabilityService);
  private cartService: CartService = inject(CartService);
  private configurationService: ConfigurationService =
    inject(ConfigurationService);
  private dvmService: DvmService = inject(DvmService);
  private isolatedSeatsService: IsolatedSeatsService =
    inject(IsolatedSeatsService);
  private tutorialService: TutorialService = inject(TutorialService);
  private utilitiesService: UtilitiesService = inject(UtilitiesService);
  needADA: boolean = false;
  subscriptions: Subscription[] = [];
  viewer: MapViewer;

  /**
   * Initializes the service and subscribes to tutorial events.
   * Handles specific tutorial steps related to seat selection and cleanup.
   */
  constructor() {
    const tutorial$ = this.tutorialService.tutorial$.subscribe(
      tutorialConfig => {
        if (tutorialConfig.activeStepCommand === 'setSeatsInCart') {
          const selectedSeatNodes =
            this.tutorialService.getTutorialSelectedSeatNodes();

          this.selectAvailableSeats(
            selectedSeatNodes.map(node => ({
              id: node.id,
              original_id: node.original_id,
            }))
          );
        }

        if (tutorialConfig.isTutorialEnded) {
          this.unselectAllSelectedSeats();
        }
      }
    );
    this.dvmService.viewerSubscriptions.push(tutorial$);
  }

  /**
   * Cleans up subscriptions when the service is destroyed.
   */
  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  /**
   * Selects one or more available seats on the viewer and adds them to the cart.
   * If multiple seats are being selected at once (e.g., best available, area selection),
   * it first clears any previously selected seats.
   * @param nodeIds - An array of seat IDs (CombinedIds format) to select.
   */
  selectAvailableSeats(nodeIds: CombinedIds[]) {
    // Reset selectedSeats if you are adding a group of seats (best available or area selection)
    if (nodeIds.length > 1) {
      this.dvmService.viewerService.unselectAll();
      this.cartService.selectedSeats = {};
      this.cartService.selectedSeats$.next(this.cartService.selectedSeats);
    }

    let ids = nodeIds.map(node => node.id);
    // Select Seat(s) on Viewer
    this.dvmService.viewerService.select(ids);

    this.selectSeatsForCart(nodeIds);
  }

  /**
   * Unselects specified seats on the viewer, removes them from the cart,
   * recalculates cart totals, and updates the cart data.
   * @param nodeIds - An array of seat node objects (containing at least 'id') to unselect.
   */
  unselectSelectedSeats(
    nodeIds: { id: string; original_id?: string; originalId?: string }[]
  ) {
    // Unselect Seat on Viewer
    let success = this.dvmService.viewerService.unselect(nodeIds);

    for (const nodeId of nodeIds) {
      // Remove one new seat (in Cart Service)
      let isDeleted = delete this.cartService.selectedSeats[nodeId.id];
      this.cartService.selectedSeats$.next(this.cartService.selectedSeats);
    }

    const { totalFee, totalPrice, totalPriceWithFees } =
      this.utilitiesService.totalSumDvmSeatsPrices(
        this.cartService.selectedSeats
      );
    this.cartService.selectedSeatsTotalPrice = totalPrice;
    this.cartService.selectedSeatsTotalFee = totalFee;
    this.cartService.selectedSeatsTotalPriceWithFees = totalPriceWithFees;

    // Update cart Seats data
    this.updateCartSeatsBySection();
  }

  /**
   * Unselects all currently selected seats on the viewer, clears the cart completely,
   * resets cart totals and data, and navigates back to the top view if currently in a seat view.
   * This is typically used for actions like clearing the entire selection or starting over.
   */
  unselectAllSelectedSeats() {
    let success = this.dvmService.viewerService.unselectAll();

    // Reset new seats (in Transaction Service)
    this.cartService.selectedSeats = {};
    this.cartService.selectedSeats$.next(this.cartService.selectedSeats);

    const { totalFee, totalPrice, totalPriceWithFees } =
      this.utilitiesService.totalSumDvmSeatsPrices(
        this.cartService.selectedSeats
      );
    this.cartService.selectedSeatsTotalPrice = totalPrice;
    this.cartService.selectedSeatsTotalFee = totalFee;
    this.cartService.selectedSeatsTotalPriceWithFees = totalPriceWithFees;

    // Update cart Seats data
    this.updateCartSeatsBySection();

    this.cartService.resetCart();

    // If the user is currently zoomed into a section (not in top view), navigate back to the top view.
    if (!this.dvmService.isTopView()) {
      const subscription = this.dvmService.openSectionMap().subscribe();
      this.subscriptions.push(subscription);
    }
  }

  /**
   * Unselects all currently selected seats on the viewer and clears the cart,
   * similar to `unselectAllSelectedSeats`, but *without* triggering navigation
   * back to the top view. Useful for internal cleanup where map navigation is not desired.
   */
  unselectAllSelectedSeatsWithoutLoadingMap() {
    const selectedSeats = this.dvmService.viewerService.getNodes({
      type: ['seat'],
      state: 'selected',
    });
    this.dvmService.viewerService.unselect(selectedSeats);
    // Reset new seats (in Transaction Service)
    this.cartService.selectedSeats = {};
    this.cartService.selectedSeats$.next(this.cartService.selectedSeats);

    const { totalFee, totalPrice, totalPriceWithFees } =
      this.utilitiesService.totalSumDvmSeatsPrices(
        this.cartService.selectedSeats
      );
    this.cartService.selectedSeatsTotalPrice = totalPrice;
    this.cartService.selectedSeatsTotalFee = totalFee;
    this.cartService.selectedSeatsTotalPriceWithFees = totalPriceWithFees;

    // Update cart Seats data
    this.updateCartSeatsBySection();
  }

  /**
   * Adds detailed information for the specified seats to the cart service.
   * It fetches pricing details from the AvailabilityService for each seat,
   * updates the cart's selected seats map, recalculates totals, and updates
   * the section-based grouping of seats.
   * @param nodeIds - An array of seat IDs (either CombinedIds format or simple strings) to add to the cart.
   */
  selectSeatsForCart(nodeIds: CombinedIds[] | string[]) {
    // Add seats to Cart
    for (const node of nodeIds) {
      // Merging new seat info from two objects:
      const newSeat = this.utilitiesService.splittedSeatInfoFromId(
        node
      ) as Seat;
      const id = typeof node === 'string' ? node : node.id;

      // Fetch detailed seat info, including price tiers, from the availability data.
      const seatInfo = this.availabilityService.getSeatByIdFromAvailability(id);

      newSeat.price = seatInfo?.price?.[0]?.value; // The first price is the default buyer type price.
      newSeat.totalFee = seatInfo?.price?.[0]?.totalFee; // The first price is the default buyer type price.
      newSeat.totalPriceWithFees = seatInfo?.price?.[0]?.totalPriceWithFees; // The first price is the default buyer type price.
      // Add one new seat (in Cart Service)
      this.cartService.selectedSeats[newSeat.id] = newSeat;
      // Notify subscribers about the change in selected seats.
      this.cartService.selectedSeats$.next(this.cartService.selectedSeats);
    }

    // Update cart Seats data
    this.updateCartSeatsBySection();

    const { totalFee, totalPrice, totalPriceWithFees } =
      this.utilitiesService.totalSumDvmSeatsPrices(
        this.cartService.selectedSeats
      );
    this.cartService.selectedSeatsTotalPrice = totalPrice;
    this.cartService.selectedSeatsTotalFee = totalFee;
    this.cartService.selectedSeatsTotalPriceWithFees = totalPriceWithFees;
  }

  /**
   * Updates the cart service's representation of selected seats grouped by section.
   * It recalculates the `selectedSeatsBySection` map, creates a sorted array
   * `sortedSelectedSeatsBySectionArray`, notifies subscribers of changes, and
   * cleans up any state related to isolated seats or pending selections on the viewer.
   */
  updateCartSeatsBySection() {
    this.cartService.selectedSeatsBySection =
      this.utilitiesService.distributeSeatsBySection(
        Object.values(this.cartService.selectedSeats)
      );

    this.cartService.sortedSelectedSeatsBySectionArray =
      this.utilitiesService.sortSectionsBySeatsQuantity(
        this.cartService.selectedSeatsBySection
      );
    this.cartService.sortedSelectedSeatsBySectionArray$.next(
      this.cartService.sortedSelectedSeatsBySectionArray
    );

    this.cartService.selectedSeats$.next(this.cartService.selectedSeats);
    // Reset isolated seats state as the cart has been updated.
    this.isolatedSeatsService.isolatedSeatsActive = false;

    this.isolatedSeatsService.lastIsolatedSeats = null;
    this.dvmService.viewerService.removeNodesFromGroup(
      this.dvmService.viewerService.getNodesByGroups('seat', 'pending'),
      'pending'
    );
  }

  /**
   * Updates the visual state of sections on the main viewer (top view) and the minimap
   * to reflect which sections currently contain selected seats. It highlights these sections.
   * Interacts with viewer groups and potentially the minimap service.
   * Assumes the viewer is in the top view state for section highlighting to be relevant.
   */
  updateSelectedSectionsOnTopview() {
    // Get selected sections from cartService
    const selectedSeatsSectionIds = Object.values(
      this.cartService.selectedSeatsBySection
    );
    const nodeList = selectedSeatsSectionIds.map(item => item.id);
    // Update selected sections on Topview
    // Clear previous section highlights (assuming 'group1' is used for this purpose)
    const previouslyHighlightedNodes =
      this.dvmService.viewerService.getNodesByGroups('section', 'group1');
    this.dvmService.viewerService.removeNodesFromGroup(
      previouslyHighlightedNodes,
      'group1'
    );
    this.dvmService.viewerService.addNodesToGroup(nodeList, 'group1');

    nodeList.forEach(node => {
      // Ensure the section nodes are visually marked as available/selectable if needed
      this.dvmService.viewerService.setAvailable('section', node);
    });

    const dvmData = this.configurationService.configuration.dvmData;

    // Update selected sections on Mini Map
    if (dvmData.miniMapEnabled && dvmData.miniMapId) {
      this.dvmService.minimapService.unselectAll();
      let success = this.dvmService.minimapService.select(
        selectedSeatsSectionIds
      );
      let selectedMini = this.dvmService.minimapService.getNodesByState(
        'section',
        'selected'
      );
    }
  }
}
